import React, {
  useState,
  useMemo,
  useEffect,
  memo
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import GoogleMapsGallery from '../Gallery/GoogleMapsGallery';
import { REGION_CONFIG } from '../../config/region';

/* ---------------- Speed Badge (Download & Upload) ---------------- */

const SpeedBadge = memo(
  ({ downloadSpeed, uploadSpeed }) => (
    <div
      style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(8px)',
        color: 'white',
        padding: '6px 12px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        maxWidth: '60vw',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <ArrowDownIcon style={{ width: 14, height: 14, color: '#10b981' }} />
        <span>{downloadSpeed} Mb/s</span>
      </div>
      <div style={{ width: '1px', height: '14px', backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <ArrowUpIcon style={{ width: 14, height: 14, color: '#3b82f6' }} />
        <span>{uploadSpeed} Mb/s</span>
      </div>
    </div>
  ),
  (prevProps, nextProps) => 
    prevProps.downloadSpeed === nextProps.downloadSpeed &&
    prevProps.uploadSpeed === nextProps.uploadSpeed
);
SpeedBadge.displayName = 'SpeedBadge';

/* Normalize a Neon venue_media row (snake_case) → camelCase expected by GoogleMapsGallery */
function normalizeNeonMedia(items) {
  return items
    .filter(m => !m.is_video)
    .map(m => ({
      _id: String(m.id),
      fileUrl: m.file_url,
      thumbnailUrl: m.file_url,
      s3Key: m.s3_key || null,
      is360: !!m.is_360,
      isVideo: false,
      mediaType: m.media_type || (m.is_360 ? 'PANO_360' : null),
    }));
}

/* ---------------- Main component ---------------- */

// neonMedia: optional array of Neon venue_media rows — when provided, skips MongoDB fetch
const VenueGallery = ({ venue, neonMedia }) => {
  const { t } = useTranslation();
  // If neonMedia is supplied, pre-initialize with normalized data and mark loading done
  const [venueMedia, setVenueMedia] = useState(() =>
    neonMedia && neonMedia.length > 0 ? normalizeNeonMedia(neonMedia) : []
  );
  const [googlePhotos, setGooglePhotos] = useState([]);
  const [loading, setLoading] = useState(!neonMedia);
  const [loadingGooglePhotos, setLoadingGooglePhotos] = useState(false);
  const [error, setError] = useState(null);
  const [speedTests, setSpeedTests] = useState([]);

  // Fetch WiFi speed tests
  useEffect(() => {
    const fetchSpeedTests = async () => {
      // Use mongo_id bridge (Neon venues.mongo_id → MongoDB wifi_speed_tests.LinkVenueID)
      const venueId = venue?.mongo_id || venue?._id;

      if (!venueId || venueId === 'undefined' || venueId === 'null') {
        console.log('⚠️ No mongo_id for speed tests');
        return;
      }

      if (venueId.startsWith('ChIJ')) {
        console.log('⚠️ Skipping speed test fetch for Google Place ID');
        return;
      }

      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const url = `${apiBase}/api/wifi-speed/venue/${venueId}`;
        console.log('🔄 Fetching speed tests from:', url);

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('✅ Speed Test API Response:', data);

        if (data.success && Array.isArray(data.data)) {
          setSpeedTests(data.data);
          console.log(`📊 Loaded ${data.data.length} speed tests`);
        }
      } catch (err) {
        console.error('❌ Error fetching speed tests:', err);
      }
    };

    fetchSpeedTests();
  }, [venue?._id]);

  // Fetch Google Photos with Places API (Optimized Version)
  useEffect(() => {
    const fetchGooglePhotos = async () => {
      // Skip if venue media is already available
      if (venueMedia.length > 0) {
        console.log('✅ Venue media already available, skipping Google Photos fetch');
        return;
      }

      // Support both Neon (google_place_id) and legacy MongoDB (googleData.placeId)
      const placeId = venue?.google_place_id || venue?.googleData?.placeId;

      if (!placeId) {
        console.log('⚠️ No Google Place ID available');
        setLoadingGooglePhotos(false);
        return;
      }

      if (!placeId.startsWith('ChIJ')) {
        console.log('⚠️ Invalid Place ID format:', placeId);
        setLoadingGooglePhotos(false);
        return;
      }

      try {
        setLoadingGooglePhotos(true);

        // Priority 0: Use hero_photo from Outscraper enrichment (FREE, no API call)
        const heroPhoto = venue?.hero_photo || venue?.heroPhoto || venue?.ph;
        if (heroPhoto && heroPhoto.startsWith('http')) {
          console.log('✅ Using Outscraper hero photo (free):', heroPhoto.slice(0, 60));
          setGooglePhotos([heroPhoto]);
          setLoadingGooglePhotos(false);
          return;
        }

        console.log('🔄 Fetching Google Place photos...');
        console.log('📍 Place ID:', placeId);

        // Priority 1: Check if photos already exist in venue.googleData.photos
        if (venue?.googleData?.photos && venue.googleData.photos.length > 0) {
          console.log('✅ Found existing Google photos in venue data');
          
          const existingPhotos = venue.googleData.photos;
          
          // If photos are already URLs, use them directly
          if (typeof existingPhotos[0] === 'string' && existingPhotos[0].startsWith('http')) {
            console.log(`✅ Using ${existingPhotos.length} existing photo URLs`);
            setGooglePhotos(existingPhotos);
            setLoadingGooglePhotos(false);
            return;
          }

          // If photos have photo_reference, construct URLs directly
          const GOOGLE_API_KEY = 'AIzaSyAwyoXxlMU6ta3V_5WV8ywxGtMZr0YqmGk';
          const photoUrls = existingPhotos
            .slice(0, 10)
            .map(photo => {
              const photoRef = photo.photo_reference || 
                             photo.photoreference || 
                             photo.photoReference || 
                             photo;
              
              if (typeof photoRef === 'string' && photoRef.length > 20) {
                return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=800&photoreference=${photoRef}&key=${GOOGLE_API_KEY}`;
              }
              return null;
            })
            .filter(url => url !== null);

          if (photoUrls.length > 0) {
            console.log(`✅ Constructed ${photoUrls.length} photo URLs from references`);
            setGooglePhotos(photoUrls);
            setLoadingGooglePhotos(false);
            return;
          }
        }

        // Priority 2: Use Google Maps JavaScript API (no CORS issues)
        if (window.google && window.google.maps && window.google.maps.places) {
          try {
            console.log('🔄 Using Google Maps JavaScript API...');
            
            // Use the new Places Library (v3.56+)
            const { Place } = await window.google.maps.importLibrary("places");
            
            const place = new Place({
              id: placeId,
              requestedLanguage: 'en',
            });

            await place.fetchFields({
              fields: ['photos']
            });

            if (place.photos && place.photos.length > 0) {
              const photoUrls = place.photos.slice(0, 10).map(photo => 
                photo.getURI({ maxWidth: 800, maxHeight: 800 })
              );
              
              console.log(`✅ Loaded ${photoUrls.length} Google photo URLs via JavaScript API`);
              setGooglePhotos(photoUrls);
              setLoadingGooglePhotos(false);
              return;
            } else {
              console.log('⚠️ No photos found in Place object');
            }
          } catch (jsApiError) {
            console.warn('⚠️ Google Maps JavaScript API failed:', jsApiError);
          }
        }

        // Priority 3: Construct URLs from Place Details API (requires backend proxy due to CORS)
        // This is a fallback that constructs photo URLs if we can get photo_references
        console.log('⚠️ Google Maps JavaScript API not available');
        console.log('💡 To load Google Photos, either:');
        console.log('   1. Include Google Maps JavaScript API: <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_KEY&libraries=places"></script>');
        console.log('   2. Use backend proxy to fetch Place Details');
        console.log('   3. Store photo URLs in venue.googleData.photos when creating venue');

      } catch (err) {
        console.error('❌ Error fetching Google Place photos:', err);
      } finally {
        setLoadingGooglePhotos(false);
      }
    };

    fetchGooglePhotos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.googleData?.photos, venue?.google_place_id, venue?.googleData?.placeId, venueMedia.length]);

  // Fetch venue media from backend — skipped when neonMedia prop is provided
  useEffect(() => {
    if (neonMedia) return; // Already initialized from neonMedia prop
    const fetchVenueMedia = async () => {
      // Use mongo_id (bridge from Neon venues → MongoDB venuemedia.LinkVenueID)
      const venueId = venue?.mongo_id || venue?._id;

      if (!venueId || venueId === 'undefined' || venueId === 'null') {
        console.error('❌ No mongo_id on venue', { venue });
        setError(t('gallery.noValidVenueId'));
        setLoading(false);
        return;
      }

      if (venueId.startsWith('ChIJ')) {
        console.error('❌ Google Place ID detected - venue not yet created in database');
        setError(t('gallery.venueNotOnboarded'));
        setLoading(false);
        return;
      }

      return fetchMedia(venueId);
    };

    const fetchMedia = async (venueId) => {
      try {
        setLoading(true);
        setError(null);

        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const url = `${apiBase}/api/venues/${venueId}/media`;
        console.log('🔄 [VenueGallery] Fetching media from:', url);
        console.log('🔍 [VenueGallery] Using venues._id:', venueId);
        console.log('🔍 [VenueGallery] Backend will query: venuemedia.LinkVenueID = venues._id');

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'x-region': REGION_CONFIG.region || 'th'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `HTTP ${response.status}: ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('✅ Media API Response:', data);

        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch venue media');
        }

        const media = data.data || [];

        // Normalize: Neon returns mediaTypes[] (array from media_type_tags table)
        // but GoogleMapsGallery filters/tabs use item.mediaType (singular string).
        // Also map is360 boolean → PANO_360 so Panellum tab picks them up.
        const normalized = media.map(m => ({
          ...m,
          mediaType: m.mediaType
            || (Array.isArray(m.mediaTypes) && m.mediaTypes[0])
            || (Array.isArray(m.aiMediaTypes) && m.aiMediaTypes[0])
            || (m.is360 ? 'PANO_360' : null)
            || null,
        }));

        const images = normalized.filter((m) => {
          // Check for any valid image URL (s3Key, fileUrl, mediumUrl, or thumbnailUrl)
          const hasUrl = m.s3Key || m.fileUrl || m.mediumUrl || m.thumbnailUrl;
          const isImage = !m.isVideo;
          return isImage && hasUrl;
        });

        console.log(`📸 Found ${images.length} venue media images out of ${media.length} media items`);

        if (images.length === 0) {
          console.warn('⚠️ No venue media images found - will try Google Photos');
        }

        setVenueMedia(images);
        console.log(`🏷️ Media types found: ${[...new Set(images.map(i => i.mediaType).filter(Boolean))].join(', ')}`);
        setError(null);
      } catch (err) {
        console.error('❌ Error fetching venue media:', err);
        setVenueMedia([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVenueMedia();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?._id, neonMedia]);

  const downloadSpeed = useMemo(() => {
    if (speedTests.length > 0) {
      const validTests = speedTests.filter(test => 
        test.downloadMbps && 
        typeof test.downloadMbps === 'number' &&
        !isNaN(test.downloadMbps)
      );
      
      if (validTests.length > 0) {
        const sum = validTests.reduce((acc, test) => acc + test.downloadMbps, 0);
        const average = sum / validTests.length;
        console.log(`📊 Average download speed: ${average.toFixed(1)} Mbps from ${validTests.length} tests`);
        return average.toFixed(1);
      }
    }
    
    const fallbackSpeed = 120; /* field removed - no Neon equivalent yet for DL_SPeed_MBPS */
    console.log(`📊 Using fallback download speed: ${fallbackSpeed} Mbps`);
    return fallbackSpeed;
  }, [speedTests]);

  const uploadSpeed = useMemo(() => {
    if (speedTests.length > 0) {
      const validTests = speedTests.filter(test => 
        test.uploadMbps && 
        typeof test.uploadMbps === 'number' &&
        !isNaN(test.uploadMbps)
      );
      
      if (validTests.length > 0) {
        const sum = validTests.reduce((acc, test) => acc + test.uploadMbps, 0);
        const average = sum / validTests.length;
        console.log(`📊 Average upload speed: ${average.toFixed(1)} Mbps from ${validTests.length} tests`);
        return average.toFixed(1);
      }
    }
    
    const fallbackSpeed = venue?.ULSPeedMBPS || venue?.UL_Speed_MBPS || 80;
    console.log(`📊 Using fallback upload speed: ${fallbackSpeed} Mbps`);
    return fallbackSpeed;
  }, [speedTests, venue?.ULSPeedMBPS, venue?.UL_Speed_MBPS]);
  
  const venueName = useMemo(
    () => venue?.name || venue?.venueName || t('venue.venueFallback'),
    [venue?.name, venue?.venueName, t]
  );

  // Prepare media for GoogleMapsGallery - must be before conditional returns
  // Combine venue media with Google Photos fallback
  const galleryMediaData = useMemo(() => {
    if (venueMedia.length > 0) {
      return venueMedia;
    }
    // Convert Google Photos URLs to media format
    if (googlePhotos.length > 0) {
      return googlePhotos.map((url, idx) => ({
        _id: `google-${idx}`,
        fileUrl: url,
        thumbnailUrl: url,
        isVideo: false,
      }));
    }
    return [];
  }, [venueMedia, googlePhotos]);

  if (loading || loadingGooglePhotos) {
    return (
      <div className="venue-gallery-root">
        <div className="venue-gallery-main loading-state">
          <div className="spinner-lg" />
          <div className="loading-text">
            {loadingGooglePhotos ? t('gallery.loadingPhotos') : t('gallery.loadingGallery')}
          </div>
        </div>
        <style>{baseStyles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="venue-gallery-root">
        <div className="venue-gallery-main error-state">
          <div className="error-emoji">⚠️</div>
          <div className="error-title">{t('gallery.failedToLoad')}</div>
          <div className="error-message">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="reload-btn"
          >
            {t('gallery.reloadPage')}
          </button>
        </div>
        <style>{baseStyles}</style>
      </div>
    );
  }

  // Use the new Google Maps style gallery
  return (
    <div className="venue-gallery-root" data-testid="venue-gallery">
      {/* New Google Maps Style Gallery (tabs are inside) */}
      <GoogleMapsGallery 
        media={galleryMediaData} 
        venueName={venueName}
        speedBadge={
          downloadSpeed && uploadSpeed ? (
            <SpeedBadge downloadSpeed={downloadSpeed} uploadSpeed={uploadSpeed} />
          ) : null
        }
      />

      <style>{baseStyles}</style>
    </div>
  );
};

const baseStyles = `
  .venue-gallery-root {
    position: relative;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto 40px auto;
    padding: 0 16px;
    box-sizing: border-box;
  }

  .venue-gallery-main {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    background-color: #f3f4f6;
  }

  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 18px;
    text-align: center;
    box-sizing: border-box;
    min-height: 300px;
  }

  .loading-state {
    background-color: #f3f4f6;
    border-radius: 18px;
  }

  .spinner-lg {
    width: 40px;
    height: 40px;
    border: 4px solid #f59e0b;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .loading-text {
    margin-top: 10px;
    color: #4b5563;
    font-size: 14px;
  }

  .error-state {
    background-color: #fee;
    border: 2px solid #fc8181;
    border-radius: 18px;
    color: #c53030;
  }

  .error-emoji {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }
  .error-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  .error-message {
    font-size: 0.9rem;
    max-width: 420px;
  }
  .reload-btn {
    margin-top: 12px;
    padding: 8px 16px;
    background-color: #c53030;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

export default memo(VenueGallery, (prevProps, nextProps) => {
  return (
    prevProps.venue?.name === nextProps.venue?.name &&
    prevProps.venue?._id === nextProps.venue?._id &&
    prevProps.neonMedia === nextProps.neonMedia
  );
});